import React, { useEffect, useState } from "react";

import { useFormikContext } from "formik";

import { PointOfViewEnum, SourceEnum } from "../../../../enums/template";
import { Template } from "../../../../models";

import InfoTooltip from "../../../../components/common/InfoTooltip";
import Input from "../../../../components/form/Input";
import RadioGroup, { Option } from "../../../../components/form/RadioGroup";
import TextArea from "../../../../components/form/TextArea";
import SwitchContainer from "../common/SwitchContainer";

export const pointOfViewOptions = [
  {
    id: PointOfViewEnum.THIRD_PERSON,
    name: "Third-person",
  },
  {
    id: PointOfViewEnum.FIRST_PERSON,
    name: "First-person",
  },
  {
    id: PointOfViewEnum.SECOND_PERSON,
    name: "Second-person",
  },
];

const PointOfView = () => {
  const { values, setFieldValue } = useFormikContext<Partial<Template>>();
  const [initialized, setInitialized] = useState(false);
  const [enableCtxFields, setEnableCtxFields] = useState(false);

  useEffect(() => {
    if (
      !initialized &&
      !enableCtxFields &&
      (values?.isSpeakerTheAuthor ||
        values?.speakerName ||
        values?.speakerAbout)
    ) {
      setEnableCtxFields(true);
      setInitialized(true);
    }
  }, [
    initialized,
    enableCtxFields,
    values?.isSpeakerTheAuthor,
    values?.speakerName,
    values?.speakerAbout,
  ]);

  const isBestItems = values?.sourceId === SourceEnum.BEST_ITEMS;
  const FIRST_PERSON_POV =
    values?.pointOfViewId === PointOfViewEnum.FIRST_PERSON;

  const isTopItems = values?.sourceId === SourceEnum.TOP_ITEMS;

  const availableOptions = isBestItems
    ? pointOfViewOptions.filter((o) => o.id === PointOfViewEnum.FIRST_PERSON)
    : isTopItems
      ? pointOfViewOptions
      : pointOfViewOptions.filter(
          (o) => o.id !== PointOfViewEnum.SECOND_PERSON
        );

  return (
    <div className="w-full">
      <h3 className="mb-2 tracking-wide font-medium leading-6 text-gray-900 text-xs flex items-center gap-2 uppercase">
        Point of view
        <InfoTooltip id="pointOfViewId">
          <div className="flex flex-col gap-4">
            <div>
              First-person:
              <div className="ml-6">
                <div>
                  A more opinionated point of view — conveys the perspective and
                  thoughts of the speaker or writer.
                </div>
                <div>Pronouns: I, me, my, mine, we, our, us.</div>
              </div>
            </div>
            {isTopItems && (
              <div>
                Second-person:
                <div className="ml-6">
                  <div>
                    Advisory tone that speaks directly to the reader and still
                    makes clear recommendations, without personal claims.
                  </div>
                  <div>Pronouns: you, your, yours.</div>
                </div>
              </div>
            )}
            {!isBestItems && (
              <div>
                Third-person:
                <div className="ml-6">
                  <div>
                    A more professional point of view — conveys a neutral and
                    objective presentation of the source material.
                  </div>
                  <div>
                    Pronouns: he, she, it, him, her, his, hers, they, them,
                    their, theirs.
                  </div>
                </div>
              </div>
            )}
          </div>
        </InfoTooltip>
      </h3>
      <RadioGroup
        options={availableOptions}
        name="pointOfViewId"
        selected={values?.pointOfViewId}
        onClick={({ id }: Option) => setFieldValue("pointOfViewId", id)}
      />
      {!enableCtxFields && (
        <button
          type="button"
          className="mt-6 uppercase text-2xs text-primary disabled:text-gray-500 disabled:cursor-not-allowed font-semibold"
          onClick={() => setEnableCtxFields(true)}
        >
          Add Speaker Context
        </button>
      )}

      {enableCtxFields && (
        <div className="mt-6 space-y-4 w-full">
          {FIRST_PERSON_POV && !isBestItems && !isTopItems && (
            <SwitchContainer
              title="I am the speaker in the source"
              enabled={values?.isSpeakerTheAuthor || false}
              setEnabled={() =>
                setFieldValue("isSpeakerTheAuthor", !values?.isSpeakerTheAuthor)
              }
              className="mt-6"
            />
          )}
          <Input
            label="Speaker Name"
            name="speakerName"
            value={values?.speakerName ?? ""}
            placeholder="Jane Doe"
            maxLength={200}
            onChange={({ target: { value } }) =>
              setFieldValue("speakerName", value)
            }
            onBlur={({ target: { value } }) => {
              setFieldValue("speakerName", value.trim());
            }}
          />
          <TextArea
            label="About the speaker"
            name="speakerAbout"
            value={values?.speakerAbout ?? ""}
            placeholder="example: Jane Doe is a fitness coach."
            maxLength={500}
            maxHeight={150}
            showCharacterCount={true}
            onChange={({ target: { value } }) =>
              setFieldValue("speakerAbout", value)
            }
          />
        </div>
      )}
    </div>
  );
};

export default PointOfView;
